import { isStringNullOrEmpty } from '../../Components/AnnotationManager/Annotation/AnnotationForm/Utils';
import { BreadcrumbNodeType } from '../../Components/AnnotationManager/Annotation/Breadcrumb/types';
import { IParser, ParserResult } from './factory';
import { Parser } from './parser';

export type WindTurbineParams = {
  site: string;
  inspection: string;
  turbine: string;
  blade: string;
  side: string;
  imageName: string;
  findingId: string;
};

const SEQUENCE_PATH_PARTS_COUNT = 5;
const IMAGE_PATH_PARTS_COUNT = 6;

export const VBAI_FINDING_PROPERTIES = ['Layer', 'Severity'];

export class WindTurbineParser extends Parser implements IParser<WindTurbineParams> {
  name: string;
  requiredPath = ':site/:inspection/:turbine';
  optionalPath = ':blade?/:side?/:imageName?/:findingId?';

  constructor(name: string) {
    super();
    this.name = name;
  }

  hasImagePathChanged(params: WindTurbineParams, previousPath: string): boolean {
    if (isStringNullOrEmpty(params.imageName)) {
      return true;
    }

    return `${this.getPathUntilImage(params)}/${this.getImageName(params)}` !== previousPath;
  }

  hasSequencePathChanged(params: WindTurbineParams, previousPath: string): boolean {
    return this.getPathUntilImage(params) === previousPath;
  }

  isPathSequence(path: string): boolean {
    return path.split('/').length === SEQUENCE_PATH_PARTS_COUNT;
  }

  containsSequence(path: string): boolean {
    return path.split('/').length >= SEQUENCE_PATH_PARTS_COUNT;
  }

  containsImage(path: string): boolean {
    return path.split('/').length >= IMAGE_PATH_PARTS_COUNT;
  }

  getPathUntilImage(params: WindTurbineParams): string {
    let path = `${params.site}/${params.inspection}/${params.turbine}`;

    if (isStringNullOrEmpty(params.blade)) {
      return path;
    }

    path = `${path}/${params.blade}`;

    if (isStringNullOrEmpty(params.side)) {
      return path;
    }

    path = `${path}/${params.side}`;

    return path;
  }

  parse(url: string, params: WindTurbineParams): ParserResult {
    return {
      pathObject: params,
      path: url.replace(/\/annotation-manager\//, '')
    };
  }

  parsedRequiredPath(params: WindTurbineParams): string {
    return `${params.site}/${params.inspection}/${params.turbine}`;
  }

  parsedSequencePath(params: WindTurbineParams): string {
    return `${params.site}/${params.inspection}/${params.turbine}/${params.blade}/${params.side}`;
  }

  parsedRequiredPathWithoutRoot(params: WindTurbineParams): string {
    return `${params.site}/${params.inspection}`;
  }

  parsedFullPath(params: WindTurbineParams): string {
    let path = `${params.site}/${params.inspection}/${params.turbine}`;

    if (isStringNullOrEmpty(params.blade)) {
      return path;
    }

    path = `${path}/${params.blade}`;

    if (isStringNullOrEmpty(params.side)) {
      return path;
    }

    path = `${path}/${params.side}`;

    if (isStringNullOrEmpty(params.imageName)) {
      return path;
    }

    path = `${path}/${params.imageName}`;

    if (isStringNullOrEmpty(params.findingId)) {
      return path;
    }

    path = `${path}/${params.findingId}`;

    return path;
  }

  getRootNode(params: WindTurbineParams): string {
    return params.turbine;
  }

  getBreadcrumb(params: WindTurbineParams): BreadcrumbNodeType {
    return {
      name: params.site,
      next: {
        name: params.inspection,
        next: {
          name: params.turbine,
          next: {
            name: params.blade,
            next: {
              name: params.side,
              next: {
                name: params.imageName
              }
            }
          }
        }
      }
    };
  }

  getImageName(params: WindTurbineParams): string | undefined {
    return params.imageName;
  }

  getAnnotationId(params: WindTurbineParams): string | undefined {
    return params.findingId;
  }

  isPathPartial(params: WindTurbineParams): boolean {
    return (
      isStringNullOrEmpty(params.site) ||
      isStringNullOrEmpty(params.inspection) ||
      isStringNullOrEmpty(params.turbine) ||
      isStringNullOrEmpty(params.blade) ||
      isStringNullOrEmpty(params.side) ||
      isStringNullOrEmpty(params.imageName) ||
      isStringNullOrEmpty(params.findingId)
    );
  }
}

export const getBladeAndBladeSideFromSequencePath = (sequencePath: string) => {
  const parts = sequencePath.split('/');

  if (parts.length !== 5) {
    return ['', ''];
  }

  return [parts[3], parts[4]];
};
